import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { getCurrencySymbol, toBaseCurrency, formatCurrency } from "../../lib/utils";
import { ENTRY_CATEGORIES, EXIT_CATEGORIES, METHOD_LABELS } from "../../lib/cashPrint";

const selectCls = "w-full h-9 rounded-md border border-input bg-background px-3 text-sm";

export const CashMovementDialog = ({ type, open, onClose, onSubmit, expectedCash }) => {
  const isEntry = type === "entry";
  const cats = isEntry ? ENTRY_CATEGORIES : EXIT_CATEGORIES;
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState(cats[0]);
  const [method, setMethod] = useState("cash");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("");
      setCategory(cats[0]);
      setMethod("cash");
      setDescription("");
    }
  }, [open, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const base = toBaseCurrency(amount);
    if (!base || base <= 0) return;
    setBusy(true);
    try {
      const ok = await onSubmit({ type, amount: base, category, payment_method: method, description });
      if (ok) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`cash-${type}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEntry ? <ArrowDownCircle className="w-5 h-5 text-emerald-500" /> : <ArrowUpCircle className="w-5 h-5 text-red-500" />}
            {isEntry ? "Registar Entrada" : "Registar Saída"}
          </DialogTitle>
          <DialogDescription>
            {isEntry ? "Reforço de fundo, recebimentos ou outras entradas de dinheiro." : `Sangria, despesas ou pagamentos. Disponível em numerário: ${formatCurrency(expectedCash || 0)}.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Valor ({getCurrencySymbol()}) *</Label>
              <Input type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" data-testid="cash-movement-amount" autoFocus />
            </div>
            <div className="space-y-1">
              <Label>Método</Label>
              <select className={selectCls} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="cash-movement-method">
                {["cash", "card", "transfer", "mobile", "other"].map((m) => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Categoria *</Label>
            <select className={selectCls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cash-movement-category">
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Descrição</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={isEntry ? "Ex: Reforço de trocos" : "Ex: Compra de gás, depósito no banco…"} data-testid="cash-movement-description" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={busy} className={isEntry ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-red-600 hover:bg-red-500 text-white"} data-testid="cash-movement-submit">
              {isEntry ? "Registar Entrada" : "Registar Saída"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
